import moment from "moment";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CalendarDays, Users, CalendarCheck, RefreshCw, Briefcase } from "lucide-react";
import MainLayout from "@/components/layouts";
import { SectionCards } from "@/components/shared/section-cards";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import { useMemo, useEffect, useState, useCallback } from "react";
import { useVendorDashboard } from "./dashboard.hooks";
import { vendorDashboardApi } from "./dashboard.api";
import { VendorDashboardTimeRange } from "./dashboard.api";
import AppointmentStatusBadge from "@/app/pmc/appointments/components/AppointmentStatusBadge";
import { AppointmentJoin } from "@/repositories/appointments.repository";
import { DailyWorkJoin } from "@/repositories/daily-work.repository";
import { daily_work_status } from "@/models/daily-work.model";
import VendorAppointmentAssignment from "./components/VendorAppointmentAssignment";
import { useVendorAppointments } from "./hooks/useVendorAppointments";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router";
import { permissionService } from "@/lib/permissions";
import { useAppSelector } from "@/lib/store";

function PageHeader({
  dashboardTimeRange,
  onTimeRangeChange,
  onRefresh,
  onRetry,
  showControls = true,
  isError = false,
}: {
  dashboardTimeRange: string;
  onTimeRangeChange: (value: string) => void;
  onRefresh: () => void;
  onRetry?: () => void;
  showControls?: boolean;
  isError?: boolean;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">Vendor overview and key metrics.</p>
      </div>
      {showControls && (
        <div className="flex items-center gap-2">
          <Select value={dashboardTimeRange} onValueChange={onTimeRangeChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={isError && onRetry ? onRetry : onRefresh}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {isError && onRetry ? "Retry" : "Refresh"}
          </Button>
        </div>
      )}
    </div>
  );
}

function getDailyWorkStatusBadge(status: daily_work_status) {
  const variants: Record<daily_work_status, "default" | "secondary" | "destructive"> = {
    [daily_work_status.active]: "default",
    [daily_work_status.paused]: "secondary",
    [daily_work_status.cancelled]: "destructive",
  };
  return <Badge variant={variants[status]}>{status}</Badge>;
}

export default function Home() {
  const { user } = useAppSelector((state) => state.auth);
  const { activeOrgId } = useAppSelector((state) => state.organization);
  const [dashboardPermissions, setDashboardPermissions] = useState({
    appointmentsView: false,
    dailyWorkView: false,
  });

  useEffect(() => {
    let cancelled = false;

    permissionService
      .getUserPermissions()
      .then((userPermissions) => {
        if (cancelled) return;

        const permissions = userPermissions?.permissions ?? [];
        const hasAll = permissions.includes("all");
        setDashboardPermissions({
          appointmentsView: hasAll || permissions.includes("appointments.view"),
          dailyWorkView: hasAll || permissions.includes("daily_work.view"),
        });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to load dashboard permissions:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id, activeOrgId]);

  const {
    stats,
    dashboardTimeRange,
    setDashboardTimeRange,
    loading: dashboardLoading,
    error: dashboardError,
    refreshStats,
    clearError,
  } = useVendorDashboard();

  const {
    list: allAppointments,
    updateAppointmentInList,
    refreshAppointments,
  } = useVendorAppointments(dashboardPermissions.appointmentsView);

  // State for active schedule tab
  const [activeScheduleTab, setActiveScheduleTab] = useState("today");

  // Appointments section pagination (Not Assigned / In Progress)
  const [appointmentsTab, setAppointmentsTab] = useState("not-assigned");
  const [appointmentsPage, setAppointmentsPage] = useState(1);
  const [appointmentsPageSize, setAppointmentsPageSize] = useState(5);

  // Appointments for Today / 7 days section pagination
  const [schedulePage, setSchedulePage] = useState(1);
  const [schedulePageSize, setSchedulePageSize] = useState(5);

  // Active Daily Work section: section-only range (Next 7d / 30d / 90d) and list
  const [activeDailyWorkSectionRange, setActiveDailyWorkSectionRange] = useState<VendorDashboardTimeRange>("7d");
  const [activeDailyWorkList, setActiveDailyWorkList] = useState<DailyWorkJoin[]>([]);
  const [activeDailyWorkListLoading, setActiveDailyWorkListLoading] = useState(false);

  useEffect(() => {
    if (!dashboardPermissions.dailyWorkView) {
      setActiveDailyWorkList([]);
      setActiveDailyWorkListLoading(false);
      return;
    }

    let cancelled = false;
    setActiveDailyWorkListLoading(true);
    vendorDashboardApi
      .getActiveDailyWorkList(activeDailyWorkSectionRange)
      .then(({ data }) => {
        if (!cancelled) setActiveDailyWorkList(data);
      })
      .finally(() => {
        if (!cancelled) setActiveDailyWorkListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeDailyWorkSectionRange, dashboardPermissions.dailyWorkView]);

  // Filter appointments by status and date
  const pendingAppointments = allAppointments.filter((app) => app.status === "pending");
  const inProgressAppointments = allAppointments.filter((app) => app.status === "inprogress");

  const today = new Date();
  const todayDateStr = today.toISOString().split("T")[0];
  const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
  const weekFromNowStr = weekFromNow.toISOString().split("T")[0];

  const todaysAppointments = allAppointments.filter((app) => app.date && app.date.split("T")[0] === todayDateStr);
  const weeklyAppointments = allAppointments.filter(
    (app) => app.date && app.date.split("T")[0] >= todayDateStr && app.date.split("T")[0] <= weekFromNowStr
  );

  const handleAppointmentUpdate = useCallback(
    (updatedAppointment: AppointmentJoin) => {
      updateAppointmentInList(updatedAppointment);
      refreshAppointments();
    },
    [updateAppointmentInList, refreshAppointments]
  );

  const appointmentColumns: ColumnDef<AppointmentJoin>[] = useMemo(
    () => [
      {
        header: "ID",
        accessor: (r) => r.id.slice(0, 8),
        sortable: true,
        sortKey: "id",
      },
      {
        header: "Property",
        accessor: (r) => (
          <div className="flex flex-col">
            <span className="font-medium">{r.property.name}</span>
            <span className="text-sm text-muted-foreground">
              Unit {r.property.unit_number} • Beds {r.property.beds} • Baths {r.property.baths}
            </span>
          </div>
        ),
        sortable: true,
        sortKey: "properties.name",
      },
      {
        header: "Assign Resource",
        accessor: (r) => (
          <VendorAppointmentAssignment appointment={r} onAppointmentUpdate={handleAppointmentUpdate} />
        ),
      },
      {
        header: "Service",
        accessor: (r) => (
          <>
            <div className="font-medium">
              {r.service.name} - {r.final_price ? `$${r.final_price.toFixed(2)}` : "Price TBD"}
            </div>
            <div className="text-sm text-muted-foreground">{r.service.parent_name}</div>
          </>
        ),
        sortable: true,
        sortKey: "services.name",
      },
      {
        header: "Date",
        accessor: (r) => (
          <div className="flex flex-col">
            <span className="font-medium">{moment(r.date).format("MM/DD/YYYY")}</span>
            <span className="text-sm text-muted-foreground">{moment(r.date).format("h:mm A")}</span>
          </div>
        ),
        sortable: true,
        sortKey: "date",
      },
      {
        header: "Status",
        accessor: (r) => <AppointmentStatusBadge status={r.status} />,
        sortable: true,
        sortKey: "status",
      },
    ],
    [handleAppointmentUpdate]
  );

  const activeDailyWorkColumns: ColumnDef<DailyWorkJoin>[] = useMemo(
    () => [
      {
        header: "Property",
        accessor: (r) => (
          <Link to={`/dailywork/${r.id}`} className="flex flex-col hover:underline">
            <span className="font-medium">{r.property.name}</span>
            {r.property.address && (
              <span className="text-sm text-muted-foreground">{r.property.address}</span>
            )}
          </Link>
        ),
        sortable: false,
      },
      {
        header: "Status",
        accessor: (r) => getDailyWorkStatusBadge(r.status),
      },
      {
        header: "Start Date",
        accessor: (r) => (r.start_date ? moment(r.start_date).format("MM/DD/YYYY") : "-"),
      },
      {
        header: "End Date",
        accessor: (r) => (r.end_date ? moment(r.end_date).format("MM/DD/YYYY") : "Ongoing"),
      },
      {
        header: "",
        accessor: (r) => (
          <Button variant="outline" size="sm" asChild>
            <Link to={`/dailywork/${r.id}`}>View</Link>
          </Button>
        ),
        className: "whitespace-nowrap",
      },
    ],
    []
  );

  const dynamicStats = stats
    ? [
        {
          name: "Sub Contractors",
          value: stats.totalSubContractors.toString(),
          icon: <Users size={32} strokeWidth={1} />,
        },
        {
          name: "New Appointments",
          value: stats.newAppointments.toString(),
          icon: <CalendarDays size={32} strokeWidth={1} />,
        },
        {
          name: "Completed Appointments",
          value: stats.completedAppointments.toString(),
          icon: <CalendarCheck size={32} strokeWidth={1} />,
        },
        {
          name: "Daily Work",
          value: stats.activeDailyWorkCount.toString(),
          icon: <Briefcase size={32} strokeWidth={1} />,
        },
      ]
    : [];

  const handleRetry = useCallback(() => {
    clearError();
    refreshStats();
  }, [clearError, refreshStats]);

  const breadcrumbs = [
    {
      href: "#",
      name: "Dashboard",
    },
  ];

  if (dashboardLoading && !stats) {
    return (
      <MainLayout breadcrumb={breadcrumbs}>
        <div className="space-y-6">
          <PageHeader
            dashboardTimeRange={dashboardTimeRange}
            onTimeRangeChange={(v) => setDashboardTimeRange(v as VendorDashboardTimeRange)}
            onRefresh={refreshStats}
            showControls={false}
          />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <div className="h-8 w-16 animate-pulse bg-gray-200 rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="flex justify-center py-8">
            <p className="text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (dashboardError) {
    return (
      <MainLayout breadcrumb={breadcrumbs}>
        <div className="space-y-6">
          <PageHeader
            dashboardTimeRange={dashboardTimeRange}
            onTimeRangeChange={(v) => setDashboardTimeRange(v as VendorDashboardTimeRange)}
            onRefresh={refreshStats}
            onRetry={handleRetry}
            isError
          />
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-red-600 mb-4">Failed to load dashboard: {dashboardError}</p>
                <Button onClick={handleRetry}>Try Again</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout breadcrumb={breadcrumbs}>
      <div className="space-y-6">
        <PageHeader
          dashboardTimeRange={dashboardTimeRange}
          onTimeRangeChange={(v) => setDashboardTimeRange(v as VendorDashboardTimeRange)}
          onRefresh={refreshStats}
        />

        <SectionCards data={dynamicStats} columns={4} />

        {dashboardPermissions.appointmentsView && (
        <Card>
          <CardHeader>
            <CardTitle className="uppercase">Appointments</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={appointmentsTab} onValueChange={(v) => { setAppointmentsTab(v); setAppointmentsPage(1); }}>
              <TabsList>
                <TabsTrigger value="not-assigned">Not Assigned</TabsTrigger>
                <TabsTrigger value="in-progress">In Progress</TabsTrigger>
              </TabsList>
              <TabsContent value="not-assigned">
                <SimpleTable<AppointmentJoin>
                  data={pendingAppointments.slice((appointmentsPage - 1) * appointmentsPageSize, appointmentsPage * appointmentsPageSize)}
                  totalCount={pendingAppointments.length}
                  columns={appointmentColumns}
                  page={appointmentsPage}
                  pageSize={appointmentsPageSize}
                  setPage={setAppointmentsPage}
                  setPageSize={(size) => { setAppointmentsPageSize(size); setAppointmentsPage(1); }}
                  sortKey=""
                  sortOrder="asc"
                  onSortChange={() => {}}
                  filters={[]}
                  onReset={() => {}}
                  onApply={() => {}}
                  container={true}
                />
              </TabsContent>
              <TabsContent value="in-progress">
                <SimpleTable<AppointmentJoin>
                  data={inProgressAppointments.slice((appointmentsPage - 1) * appointmentsPageSize, appointmentsPage * appointmentsPageSize)}
                  totalCount={inProgressAppointments.length}
                  columns={appointmentColumns}
                  page={appointmentsPage}
                  pageSize={appointmentsPageSize}
                  setPage={setAppointmentsPage}
                  setPageSize={(size) => { setAppointmentsPageSize(size); setAppointmentsPage(1); }}
                  sortKey=""
                  sortOrder="asc"
                  onSortChange={() => {}}
                  filters={[]}
                  onReset={() => {}}
                  onApply={() => {}}
                  container={true}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        )}

        {dashboardPermissions.appointmentsView && (
        <Card>
          <CardHeader>
            <CardTitle className="uppercase">
              {activeScheduleTab === "today" ? "Appointments for Today" : "Appointments for next 7 days"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeScheduleTab} onValueChange={(v) => { setActiveScheduleTab(v); setSchedulePage(1); }}>
              <TabsList>
                <TabsTrigger value="today">Today</TabsTrigger>
                <TabsTrigger value="7days">7 Days</TabsTrigger>
              </TabsList>
              <TabsContent value="today">
                <SimpleTable<AppointmentJoin>
                  data={todaysAppointments.slice((schedulePage - 1) * schedulePageSize, schedulePage * schedulePageSize)}
                  totalCount={todaysAppointments.length}
                  columns={appointmentColumns}
                  page={schedulePage}
                  pageSize={schedulePageSize}
                  setPage={setSchedulePage}
                  setPageSize={(size) => { setSchedulePageSize(size); setSchedulePage(1); }}
                  sortKey=""
                  sortOrder="asc"
                  onSortChange={() => {}}
                  filters={[]}
                  onReset={() => {}}
                  onApply={() => {}}
                  container={true}
                />
              </TabsContent>
              <TabsContent value="7days">
                <SimpleTable<AppointmentJoin>
                  data={weeklyAppointments.slice((schedulePage - 1) * schedulePageSize, schedulePage * schedulePageSize)}
                  totalCount={weeklyAppointments.length}
                  columns={appointmentColumns}
                  page={schedulePage}
                  pageSize={schedulePageSize}
                  setPage={setSchedulePage}
                  setPageSize={(size) => { setSchedulePageSize(size); setSchedulePage(1); }}
                  sortKey=""
                  sortOrder="asc"
                  onSortChange={() => {}}
                  filters={[]}
                  onReset={() => {}}
                  onApply={() => {}}
                  container={true}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        )}

        {dashboardPermissions.dailyWorkView && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="uppercase">Active Daily Work</CardTitle>
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground whitespace-nowrap">Range:</label>
              <Select
                value={activeDailyWorkSectionRange}
                onValueChange={(v) => setActiveDailyWorkSectionRange(v as VendorDashboardTimeRange)}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Next 7 days</SelectItem>
                  <SelectItem value="30d">Next 30 days</SelectItem>
                  <SelectItem value="90d">Next 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {activeDailyWorkListLoading ? (
              <div className="flex justify-center py-8">
                <p className="text-muted-foreground">Loading active daily work...</p>
              </div>
            ) : activeDailyWorkList.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No active daily work for this range.</p>
                <Button variant="link" asChild className="mt-2">
                  <Link to="/dailywork/contracts">View all Daily Work</Link>
                </Button>
              </div>
            ) : (
              <SimpleTable<DailyWorkJoin>
                data={activeDailyWorkList}
                totalCount={activeDailyWorkList.length}
                columns={activeDailyWorkColumns}
                page={1}
                pageSize={10}
                setPage={() => {}}
                setPageSize={() => {}}
                sortKey=""
                sortOrder="asc"
                onSortChange={() => {}}
                filters={[]}
                onReset={() => {}}
                onApply={() => {}}
                container={true}
              />
            )}
          </CardContent>
        </Card>
        )}
      </div>
    </MainLayout>
  );
}
